// ============================================
// CREATE/EDIT CERTIFICATE TYPE MODAL
// ============================================

import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { adminApi } from '@/services/api';
import { cn } from '@/utils';
import type { CertificateType } from '@crew-at-sea/shared-types';

interface CertificateTypeModalProps {
  open: boolean;
  onClose: () => void;
  editData?: CertificateType | null;
}

export function CertificateTypeModal({ open, onClose, editData }: CertificateTypeModalProps) {
  const queryClient = useQueryClient();
  const isEdit = !!editData;

  const [formData, setFormData] = useState({
    code: '',
    nameEn: '',
    nameTr: '',
    category: 'STCW',
    department: 'DECK',
    description: '',
    issuingAuthority: '',
    validityPeriod: 60,
    isRequired: false,
    sortOrder: 0,
    isActive: true,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (editData) {
      setFormData({
        code: editData.code || '',
        nameEn: editData.nameEn || '',
        nameTr: editData.nameTr || '',
        category: editData.category || 'STCW',
        department: editData.department || 'DECK',
        description: editData.description || '',
        issuingAuthority: editData.issuingAuthority || '',
        validityPeriod: editData.validityPeriod || 60,
        isRequired: editData.isRequired || false,
        sortOrder: editData.sortOrder || 0,
        isActive: editData.isActive !== false,
      });
    } else {
      setFormData({
        code: '',
        nameEn: '',
        nameTr: '',
        category: 'STCW',
        department: 'DECK',
        description: '',
        issuingAuthority: '',
        validityPeriod: 60,
        isRequired: false,
        sortOrder: 0,
        isActive: true,
      });
    }
  }, [editData, open]);

  const createMutation = useMutation({
    mutationFn: (data: typeof formData) => adminApi.createCertificateType(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'certificate-types'] });
      onClose();
    },
    onError: (error: any) => {
      setErrors({ submit: error?.response?.data?.message || 'Bir hata oluştu' });
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: typeof formData) => adminApi.updateCertificateType(editData!.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'certificate-types'] });
      onClose();
    },
    onError: (error: any) => {
      setErrors({ submit: error?.response?.data?.message || 'Bir hata oluştu' });
    },
  });

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.code) newErrors.code = 'Kod zorunludur';
    if (!formData.nameEn) newErrors.nameEn = 'İngilizce ad zorunludur';
    if (!formData.nameTr) newErrors.nameTr = 'Türkçe ad zorunludur';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      if (isEdit) {
        updateMutation.mutate(formData);
      } else {
        createMutation.mutate(formData);
      }
    }
  };

  if (!open) return null;

  const categoryOptions = [
    { value: 'STCW', label: 'STCW' },
    { value: 'FLAG', label: 'Bayrak Devleti' },
    { value: 'MEDICAL', label: 'Sağlık' },
    { value: 'SAFETY', label: 'Emniyet' },
    { value: 'TANKER', label: 'Tanker' },
    { value: 'OTHER', label: 'Diğer' },
  ];

  const departmentOptions = [
    { value: 'DECK', label: 'Güverte' },
    { value: 'ENGINE', label: 'Makine' },
    { value: 'ELECTRICAL', label: 'Elektrik' },
    { value: 'CATERING', label: 'Catering' },
    { value: 'OTHER', label: 'Diğer' },
  ];

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      
      <div className="relative w-full max-w-2xl bg-white rounded-maritime shadow-maritime-lg p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              {isEdit ? 'Sertifika Türü Düzenle' : 'Yeni Sertifika Türü'}
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              {isEdit ? 'Sertifika türü bilgilerini güncelleyin' : 'Yeni sertifika türü oluşturun'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Kod <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                placeholder="COC, GMDSS, vb."
                className={cn(
                  'w-full px-3 py-2 border rounded-maritime focus:outline-none focus:ring-2 focus:ring-maritime-500',
                  errors.code ? 'border-red-500' : 'border-gray-300'
                )}
              />
              {errors.code && <p className="text-xs text-red-500 mt-1">{errors.code}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Kategori</label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-maritime focus:outline-none focus:ring-2 focus:ring-maritime-500"
              >
                {categoryOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                İngilizce Ad <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.nameEn}
                onChange={(e) => setFormData({ ...formData, nameEn: e.target.value })}
                className={cn(
                  'w-full px-3 py-2 border rounded-maritime focus:outline-none focus:ring-2 focus:ring-maritime-500',
                  errors.nameEn ? 'border-red-500' : 'border-gray-300'
                )}
              />
              {errors.nameEn && <p className="text-xs text-red-500 mt-1">{errors.nameEn}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Türkçe Ad <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.nameTr}
                onChange={(e) => setFormData({ ...formData, nameTr: e.target.value })}
                className={cn(
                  'w-full px-3 py-2 border rounded-maritime focus:outline-none focus:ring-2 focus:ring-maritime-500',
                  errors.nameTr ? 'border-red-500' : 'border-gray-300'
                )}
              />
              {errors.nameTr && <p className="text-xs text-red-500 mt-1">{errors.nameTr}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Departman</label>
              <select
                value={formData.department}
                onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-maritime focus:outline-none focus:ring-2 focus:ring-maritime-500"
              >
                {departmentOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Veren Kurum</label>
              <input
                type="text"
                value={formData.issuingAuthority}
                onChange={(e) => setFormData({ ...formData, issuingAuthority: e.target.value })}
                placeholder="Örn: Ulaştırma Bakanlığı"
                className="w-full px-3 py-2 border border-gray-300 rounded-maritime focus:outline-none focus:ring-2 focus:ring-maritime-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Geçerlilik Süresi (Ay)</label>
              <input
                type="number"
                min="0"
                value={formData.validityPeriod}
                onChange={(e) => setFormData({ ...formData, validityPeriod: parseInt(e.target.value) || 0 })}
                className="w-full px-3 py-2 border border-gray-300 rounded-maritime focus:outline-none focus:ring-2 focus:ring-maritime-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sıralama</label>
              <input
                type="number"
                min="0"
                value={formData.sortOrder}
                onChange={(e) => setFormData({ ...formData, sortOrder: parseInt(e.target.value) || 0 })}
                className="w-full px-3 py-2 border border-gray-300 rounded-maritime focus:outline-none focus:ring-2 focus:ring-maritime-500"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Açıklama</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-maritime focus:outline-none focus:ring-2 focus:ring-maritime-500"
              />
            </div>

            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.isRequired}
                  onChange={(e) => setFormData({ ...formData, isRequired: e.target.checked })}
                  className="rounded border-gray-300 text-maritime-600 focus:ring-maritime-500"
                />
                <span className="text-sm text-gray-700">Zorunlu Sertifika</span>
              </label>

              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  className="rounded border-gray-300 text-maritime-600 focus:ring-maritime-500"
                />
                <span className="text-sm text-gray-700">Aktif</span>
              </label>
            </div>
          </div>

          {errors.submit && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-maritime">
              <p className="text-sm text-red-600">{errors.submit}</p>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-maritime"
            >
              İptal
            </button>
            <button
              type="submit"
              disabled={isPending}
              className="px-4 py-2 text-sm font-medium text-white bg-maritime-600 hover:bg-maritime-700 rounded-maritime disabled:opacity-50"
            >
              {isPending ? 'Kaydediliyor...' : isEdit ? 'Güncelle' : 'Oluştur'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
